import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
  Flex,
  Span,
  Table,
  Button,
  Tooltip,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TableActions,
  OrderableTableRow,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

const TYPE_LABELS = {
  tag: 'Tag',
  organization: 'Organization',
  both: 'Tag & Organization',
  all: 'All Tickets'
};

const filterDetail = (filter) => {
  if (filter.type === 'all') { return 'All solved tickets'; }
  const parts = [];
  if (filter.tag) { parts.push(`Tag: ${filter.tag}`); }
  if (filter.organization_id) { parts.push(`Org: ${filter.organization_name || filter.organization_id}`); }
  return parts.join(', ');
};

const ZendeskTicketFiltersList = ({
  filters = [],
  settings,
  loadingIds = {},
  isSyncing = false,
  onView,
  onResync,
  onDelete,
  onAddFilter,
  onResyncAll,
  isAddFilterLoading,
  pagination = {},
  hasPending
}) => {
  const { meta } = pagination;
  const hasLockedTickets = filters.some(filter => filter.tickets_status > 0);
  const order = useOrderableTableReducer({ id: 'zendesk-ticket-filters' });

  const isAnyLoading = Object.values(loadingIds || {}).some(Boolean);
  const isDisabled = !settings.status || hasLockedTickets || isAnyLoading || isSyncing || hasPending;

  const sortedFilters = React.useMemo(() => {
    if (!order.params.order) { return filters; }
    const sorted = [...filters].sort((a, b) => {
      const dir = order.params.direction === 'descending' ? -1 : 1;
      const key = order.params.order;
      if (a[key] < b[key]) { return -1 * dir; }
      if (a[key] > b[key]) { return 1 * dir; }
      return 0;
    });
    return sorted;
  }, [filters, order.params]);

  return (
    <Flex col>
      <Flex border xScroll>
        <Table>
          <TableHead>
            <OrderableTableRow
              rows={[
                { label: 'Filter', sort: 'name' },
                { label: 'Type' },
                { label: 'Details' },
                { label: 'Last Sync', sort: 'updated_at' },
                null
              ]}
              {...order.props}
            />
          </TableHead>
          <TableBody>
            {sortedFilters.map(filter => (
              <TableRow key={filter.id}>
                <TableCell>{filter.name}</TableCell>
                <TableCell>{TYPE_LABELS[filter.type] ?? filter.type}</TableCell>
                <TableCell>{filterDetail(filter)}</TableCell>
                <TableCell>{filter.updated_at}</TableCell>
                <TableActions>
                  <Button
                    variant="secondary"
                    icon="eye"
                    onClick={() => onView(filter.id)}
                  >
                    View
                  </Button>

                  <PermissionFilter can="edit generative ai resource">
                    <PermissionFilter can="edit generative ai zendesk tickets">
                      <Button
                        variant="secondary"
                        icon="refresh"
                        disabled={isDisabled}
                        onClick={() => onResync(filter)}
                      >
                        Resync
                      </Button>

                      <Button
                        variant="secondary"
                        icon="trash"
                        colour="danger"
                        disabled={isDisabled}
                        onClick={() => onDelete(filter)}
                      >
                        Delete
                      </Button>
                    </PermissionFilter>
                  </PermissionFilter>
                </TableActions>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Flex>

      {typeof meta.current_page === 'number' && (
        <Pagination pagination={meta} {...pagination.props} />
      )}

      <PermissionFilter can="edit generative ai resource">
        <PermissionFilter can="edit generative ai zendesk tickets">
          <Flex center wrap="true" gap="2" right mt>
            <Button
              ml
              icon="plus"
              variant="secondary"
              disabled={isDisabled}
              onClick={onAddFilter}
              loading={isAddFilterLoading}
            >
              Add Filter
            </Button>
            <Button
              ml
              icon="refresh"
              variant="secondary"
              disabled={isDisabled}
              onClick={onResyncAll}
            >
              Sync All
            </Button>
          </Flex>
        </PermissionFilter>
      </PermissionFilter>

      <Flex center right mt>
        <Span light tinyText>
          <Tooltip
            position="RIGHT"
            tooltip={<Tooltip.Content>{dayjs(settings?.updated_at).format('YYYY-MM-DD HH:mm:ss')}</Tooltip.Content>}
          >
            Last sync {dayjs(settings?.updated_at).from(dayjs())}
          </Tooltip>
        </Span>
      </Flex>
    </Flex>
  );
};

ZendeskTicketFiltersList.propTypes = {
  filters: PropTypes.array.isRequired,
  settings: PropTypes.object,
  loadingIds: PropTypes.object,
  isSyncing: PropTypes.bool,
  onView: PropTypes.func.isRequired,
  onResync: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddFilter: PropTypes.func.isRequired,
  onResyncAll: PropTypes.func.isRequired,
  isAddFilterLoading: PropTypes.bool,
  pagination: PropTypes.object,
  hasPending: PropTypes.bool.isRequired
};

export default ZendeskTicketFiltersList;
